/** @jsx jsx */

import MarkdownToJSX from 'markdown-to-jsx';
import { ReactElement, Children } from 'react';

import { jsx } from '@emotion/core';
import {
  Colors,
  Img,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableTd,
  TableTh,
} from '@zapier/design-system';

import Link from 'app/common/components/Link';

type Props = {
  /** markdown source string */
  source: string;
  /** CSS to be applied to code tags inside pre */
  codeBlockCss?: any;
  /** Css to be applied to span tags */
  spanCss?: any;
  /** CSS to be applied to code tags lines */
  codeLineCss?: any;
  /** Change anchor target */
  linkTarget?: '_self' | '_blank' | '_parent' | '_top';
};

type Options = { children: ReactElement; css?: any };

const Blockquote = ({ children }: Options) => {
  return (
    <blockquote
      css={{
        borderLeft: `0.25em solid ${Colors.GrayWarm3}`,
        color: Colors.GrayWarm7,
        margin: 0,
        padding: '0 1em',
      }}
    >
      {children}
    </blockquote>
  );
};

const CodeBlock = ({ children, css }: Options) => {
  return (
    <pre>
      {Array.isArray(children) ? (
        children
      ) : (
        <code css={{ whiteSpace: 'pre', ...css }}>
          {Children.only(children)}
        </code>
      )}
    </pre>
  );
};

const CodeLine = ({ children, css }: Options) => {
  return <code css={css}>{children}</code>;
};

const Span = ({ children, css }: Options) => {
  return <span css={css}>{children}</span>;
};

const Markdown = ({
  source,
  codeBlockCss,
  spanCss,
  codeLineCss,
  linkTarget,
}: Props) => {
  const options = {
    overrides: {
      a: { component: Link, props: { target: linkTarget } },
      blockquote: { component: Blockquote },
      img: { component: Img },
      pre: { component: CodeBlock, props: { css: codeBlockCss } },
      code: { component: CodeLine, props: { css: codeLineCss } },
      table: { component: Table },
      tbody: { component: TableBody },
      td: { component: TableTd },
      th: { component: TableTh },
      thead: { component: TableHead },
      tr: { component: TableRow },
      span: { component: Span, props: { css: spanCss } },
    },
  };

  return <MarkdownToJSX children={source} options={options} />;
};

export default Markdown;
